import React from "react"
import i18n from "i18next"
import axios from "axios"
import {
  SelectInput,
  NumberInput,
  TextAreaInput,
  DateInput,
  ResourceInput,
  Jsx,
} from "./utils/Input"
import { TextColumn } from "./utils/Columns"
import { PermissionLink } from "../components/PermissionLink.jsx"
import { store } from "../store"
import {
  Box,
  Tab,
  Text,
} from "grommet"
import { FilePond } from "react-filepond"
import fr_FR from "filepond/locale/fr-fr.js"
import {
  useAddMaintenanceMutation,
  useUpdateMaintenanceMutation,
  useDeleteMaintenanceMutation,
  selectAllMaintenances,
  selectMaintenanceById,
  useGetMaintenancesQuery,
} from "../redux/maintenancesSlice"
import { selectAllCustomers } from "../redux/customersSlice"
import Maintenance, {
  STATUS_ACTIVE,
  STATUS_INACTIVE,
  STATUS_CANCELED,
} from "../models/maintenance"
import { selectCustomerInstallations } from "../redux/installationsSlice"
import { selectTaskableTasks } from "../redux/tasksSlice"
import { CommentsList, SharepointUrl, TasksList } from "../components"
import { utils, writeFileXLSX } from "xlsx"
import { InstallationRessource } from "./InstallationResource.js"

export class MaintenanceRessource {
  static name = "maintenance"

  static resourcePath = "maintenance"

  static permissionPrefix = "maintenance"

  static indexTitle = "Contrats de maintenance"

  static oneTitle = "une maintenance"

  static fuseConfig = [
    {
      name: "customer.name",
      weight: 3,
    },
    {
      name: "customer.city",
      weight: 3,
    },
    {
      name: "customer.zip_code",
      weight: 3,
    },
    {
      name: "humanId",
      weight: 3,
    },
  ]

  static methods = {
    load: useGetMaintenancesQuery,
    add: useAddMaintenanceMutation,
    update: useUpdateMaintenanceMutation,
    delete: useDeleteMaintenanceMutation,
  }

  static formData;

  static getById(id) {
    const item = selectMaintenanceById(store.getState(), id)
    return new Maintenance(item)
  }

  static all() {
    return selectAllMaintenances(store.getState())
  }

  static indexButtons = (_data, user) => {
    const buttons = []

    if (user.is_admin) {
      buttons.push({
        label: "Export XLS",
        onClick: ({ data }) => {
          const worksheet = utils.json_to_sheet(data.map(maintenance => {
            const { customer, installation_id } = maintenance;
            const installation = InstallationRessource.getById(installation_id);
            const maintenanceColumns = {
              "Numéro de maintenance": maintenance.humanId,
              Statut: maintenance.getStatusLabel(),
              "Date de début": maintenance.getStartDate(),
              "Durée de la tâche en heures": maintenance.duration,
              "Récurrence en mois": maintenance.recurrence,
              "Date d'expiration": maintenance.getEndDate(),
            }
            const installationColumns = {
              "Numéro de l'installation": installation ? installation.humanId : '',
              "Date de mise en service": installation ? installation.getInstallationDate() : '',
              "Adresse de l'installation": installation ? installation.getAddressLines().join(", ") : '',
              "Prix de l'installation": installation ? installation.getMaintenancePrice() : '',
              "Nom du contact": installation.contact_name,
              "Téléphone du contact": installation.contact_phone,
            }
            const customerColumns = {
              "Numéro de client": customer.humanId,
              Nom: customer.name,
              Entreprise: customer.company,
              "Adresse du client": customer.getAddressLines({ customerName: false }).join(", "),
              Téléphone: customer.phone,
              Mobile: customer.mobile,
              Email: customer.email,
              Type: i18n.t(`customer.type.${customer.type}`)
            }
            return {
              ...maintenanceColumns,
              ...installationColumns,
              ...customerColumns
            }
          }));
          const workbook = utils.book_new();
          utils.book_append_sheet(workbook, worksheet, "Maintenances");
          writeFileXLSX(workbook, 'export-maintenances.xlsx')
        },
      })
    }

    return buttons
  }

  static form(data = {}) {
    const customers = selectAllCustomers(store.getState()).map(c => ({
      value: c.id,
      label: c.getTitle(),
    }))

    const installations = selectCustomerInstallations(
      store.getState(),
      data?.customer_id
    ).map(c => ({ value: c.id, label: c.getTitle() }))

    return {
      fields: [
        new Jsx("sharepoint")
          .label("Fichiers")
          .display(() => <SharepointUrl url={data.getDriveFolderUrl()} />)
          .get(),

        new Jsx("humanId")
          .label("Numéro de maintenance")
          .display(() => data.humanId)
          .get(),

        new ResourceInput("customer_id")
          .label("Client")
          .options(customers)
          .resourcePath("customers")
          .permission("customer_view")
          .searchable()
          .required()
          .get(),

        new ResourceInput("installation_id")
          .label("Installation")
          .options(installations)
          .resourcePath("installations")
          .permission("installation_view")
          .hidden(installations.length === 0)
          .get(),

        new SelectInput("status")
          .label("Statut")
          .required()
          .options([
            {
              value: STATUS_ACTIVE,
              label: "Actif",
            },
            {
              value: STATUS_INACTIVE,
              label: "Inactif",
            },
            {
              value: STATUS_CANCELED,
              label: "Annulé",
            },
          ])
          .get(),

        new DateInput("starts_at").label("Date de début").required().get(),

        new NumberInput("duration")
          .label("Durée de la tâche en heures")
          .labelOnDetail("Durée de la tâche")
          .display(value => `${value}h`)
          .required()
          .get(),

        new NumberInput("recurrence")
          .label("Récurrence en mois")
          .labelOnDetail("Récurrence")
          .display(value => `${value} mois`)
          .required()
          .get(),

        new DateInput("ends_at").label("Date d'expiration").get(),

        new TextAreaInput("notes").markdown().label("Notes").get(),
      ],
    }
  }

  static table() {
    return {
      columns: [
        new TextColumn("humanId")
          .label(() => (
            <>
              <div className="mobileTitle">Nom & prénom</div>
              <div className="desktopTitle">N° Contrat</div>
            </>
          ))
          .render(item => (
            <PermissionLink
              to={`/maintenances/${item.id}`}
              permission={`${this.permissionPrefix}_view`}
            >
              <div className="mobileTitle">
                {item.customer ? (
                  <>
                    <div>
                      {item.customer?.getTitle()} {item.customer?.getZipCity()}
                    </div>
                    <small style={{ color: "#777" }}>{item.humanId}</small>
                  </>
                ) : (
                  item.humanId
                )}
              </div>
              <div className="desktopTitle">{item.humanId}</div>
            </PermissionLink>
          ))
          .get(),

        new TextColumn("customer.name")
          .label("Client")
          .render(item => item?.customer?.getTitle())
          .get(),

        new TextColumn("zipcity").label("Localité").get(),

        new TextColumn("status")
          .label("Statut")
          .render(item => item.getStatusLabel())
          .get(),

        new TextColumn("start_at")
          .label("Date de début")
          .render(item => item.getStartDate())
          .get(),
      ],
    }
  }

  static tabs(data) {
    const maintenanace = new Maintenance(data);
    const tasks = selectTaskableTasks(store.getState(), "maintenance", data.id);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [files, setFiles] = React.useState([]);

    return [
      {
        title: <>{tasks.length || 0} Tâches</>,
        content: (
          <TasksList
            {...{
              tasks,
              relatable_type: "maintenance",
              RESSOURCE: "maintenance",
              id: data.id,
            }}
          />
        ),
      },
      <>
        <CommentsList
          {...{
            relatable_type: "maintenance",
            RESSOURCE: "maintenance",
            id: data.id,
          }}
        />
      </>,
      <>
        <Tab data-cy="media-tab" title={<>{"Images"}</>}>
          <Box pad={{ vertical: "medium" }}>
            <FilePond
              files={files}
              onupdatefiles={setFiles}
              allowMultiple={true}
              allowRevert={false}
              onactivatefile
              maxFileSize="1000MB"
              {...fr_FR}
              server={{
                url: `${process.env.REACT_APP_API_BASE_URL}/maintenance/${data.id}/medias`,
                process: (fieldName, file, _x, load, error, _y, abort) => {
                  const controller = new AbortController()
                  const formData = new FormData()
                  formData.append(fieldName, file)
                  formData.append("name", file.name)
                  axios
                    .post(`/maintenance/${data.id}/medias`, formData, {
                      signal: controller.signal,
                    })
                    .then(() => {
                      load()
                    })
                    .catch(() => {
                      error()
                    })

                  return {
                    abort: () => {
                      controller.abort()
                      abort()
                    },
                  }
                },
              }}
              name="media"
            />
            <Text>
              Une fois téléchargée, l'image ne s'affiche plus dans cette zone.
              Vous retrouverez les images précédemment téléchargées dans le
              Sharepoint.
            </Text>
            <SharepointUrl url={maintenanace.getDriveFolderUrl()} />
          </Box>
        </Tab>
      </>,
    ]
  }
}
